import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  MessageSquare, Plus, Search, Users, User as UserIcon, Trash2,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Loader, { EmptyState } from '@/components/Loader';
import Modal from '@/components/Modal';
import ConfirmDialog from '@/components/ConfirmDialog';
import { conversationService, userService } from '@/services';
import { useAuth } from '@/hooks/useAuth';

export default function Chats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmState, setConfirmState] = useState(null);

  const askDelete = (convo) => {
    const label = convo.type === 'group' ? (convo.name || 'this group') : 'this chat';
    setConfirmState({
      title: 'Delete chat',
      message: `"${label}" and all its messages will be permanently deleted for everyone. Continue?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await conversationService.remove(convo._id);
          toast.success('Chat deleted');
          refetch();
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed to delete');
        }
      },
    });
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => conversationService.list(),
    refetchInterval: 5_000,
  });

  const items = data?.items || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => {
      if (c.type === 'group') return (c.name || '').toLowerCase().includes(q);
      const other = c.members.find((m) => String(m._id) !== String(user?._id));
      return other?.name?.toLowerCase().includes(q) || other?.email?.toLowerCase().includes(q);
    });
  }, [items, search, user?._id]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Chats"
        subtitle="Direct messages and group chats with your team"
        actions={
          <button className="btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={16} /> New chat
          </button>
        }
      />

      <Card>
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input
            placeholder="Search by name..."
            className="input pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isLoading ? (
          <Loader />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={items.length === 0 ? 'No chats yet' : 'No matching chats'}
            subtitle={items.length === 0 ? 'Start a direct or group chat with your teammates' : undefined}
            action={items.length === 0 && (
              <button className="btn-primary" onClick={() => setShowNew(true)}>
                <Plus size={16} /> New chat
              </button>
            )}
          />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map((c) => (
              <ConversationRow
                key={c._id}
                convo={c}
                currentUser={user}
                onClick={() => navigate(`/chats/${c._id}`)}
                onDelete={() => askDelete(c)}
              />
            ))}
          </ul>
        )}
      </Card>

      {showNew && (
        <NewChatModal
          onClose={() => setShowNew(false)}
          onCreated={(c) => {
            setShowNew(false);
            refetch();
            navigate(`/chats/${c._id}`);
          }}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function ConversationRow({ convo, currentUser, onClick, onDelete }) {
  const currentUserId = currentUser?._id;
  const isGroup = convo.type === 'group';
  const other = !isGroup ? convo.members.find((m) => String(m._id) !== String(currentUserId)) : null;
  const display = isGroup ? convo.name : other?.name || 'Direct chat';
  const last = convo.lastMessage;
  const lastWho = last?.sender?.name ? `${last.sender.name}: ` : '';
  const lastText = last?.text || (last?.attachments?.length ? `📎 ${last.attachments.length} attachment(s)` : '');

  // Unread if the last message is newer than my last-read time and I didn't send it.
  const myRead = (convo.reads || []).find((r) => String(r.user) === String(currentUserId));
  const myReadAt = myRead?.at ? new Date(myRead.at).getTime() : 0;
  const lastAt = convo.lastMessageAt ? new Date(convo.lastMessageAt).getTime() : 0;
  const lastSenderId = String(last?.sender?._id || last?.sender || '');
  const unread = !!lastSenderId && lastSenderId !== String(currentUserId) && lastAt > myReadAt;

  const canDelete = String(convo.createdBy) === String(currentUserId) || currentUser?.role === 'admin';

  return (
    <li className="flex items-center group">
      <button
        onClick={onClick}
        className="flex-1 min-w-0 flex items-center gap-3 py-3 px-1 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg"
      >
        <div className={`relative w-12 h-12 rounded-full grid place-items-center text-white font-bold shrink-0 ${
          isGroup
            ? 'bg-gradient-to-br from-violet-500 to-pink-500'
            : 'bg-gradient-to-br from-brand-500 to-indigo-600'
        }`}>
          {isGroup ? <Users size={20} /> : (display?.[0]?.toUpperCase() || '?')}
          {unread && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className={`truncate ${unread ? 'font-bold' : 'font-semibold'}`}>{display}</div>
            {convo.lastMessageAt && (
              <span className={`text-[11px] shrink-0 ${unread ? 'text-brand-600 dark:text-brand-300 font-semibold' : 'text-slate-400'}`}>
                {dayjs(convo.lastMessageAt).isSame(dayjs(), 'day')
                  ? dayjs(convo.lastMessageAt).format('hh:mm A')
                  : dayjs(convo.lastMessageAt).format('DD MMM')}
              </span>
            )}
          </div>
          <div className={`text-xs truncate flex items-center gap-1 ${unread ? 'text-slate-700 dark:text-slate-200 font-medium' : 'text-slate-500'}`}>
            {isGroup && <span className="text-slate-400">{convo.members.length} members ·</span>}
            <span className="truncate">{lastWho}{lastText || (isGroup ? 'New group' : 'Say hi 👋')}</span>
          </div>
        </div>
      </button>
      {canDelete && (
        <button
          onClick={onDelete}
          title="Delete chat"
          className="shrink-0 p-2 mr-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 md:opacity-0 md:group-hover:opacity-100 transition"
        >
          <Trash2 size={16} />
        </button>
      )}
    </li>
  );
}

function NewChatModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('direct');
  const [search, setSearch] = useState('');
  const [groupName, setGroupName] = useState('');
  const [memberIds, setMemberIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const users = useQuery({
    queryKey: ['users', 'all', 'chat'],
    queryFn: () => userService.list({ limit: 200 }),
  });
  const list = (users.data?.items || []).filter((u) => String(u._id) !== String(user?._id));
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  }, [list, search]);

  const toggle = (id) => setMemberIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  const startDirect = async (other) => {
    setBusy(true);
    try {
      const res = await conversationService.createDirect(other._id);
      onCreated?.(res.conversation);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const createGroup = async () => {
    if (!groupName.trim()) return toast.error('Group name is required');
    if (memberIds.length === 0) return toast.error('Pick at least one teammate');
    setBusy(true);
    try {
      const res = await conversationService.createGroup(groupName.trim(), memberIds);
      onCreated?.(res.conversation);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New chat"
      size="lg"
      footer={tab === 'group' && (
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={createGroup} className="btn-primary" disabled={busy}>Create group</button>
        </>
      )}
    >
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setTab('direct')}
          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
            tab === 'direct'
              ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'
              : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          <UserIcon size={14} className="inline mr-1.5" /> Direct chat
        </button>
        <button
          onClick={() => setTab('group')}
          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
            tab === 'group'
              ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'
              : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          <Users size={14} className="inline mr-1.5" /> Group chat
        </button>
      </div>

      {tab === 'group' && (
        <div className="mb-3">
          <label className="label">Group name</label>
          <input className="input" placeholder="e.g. Design Team" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
        </div>
      )}

      <div className="relative mb-2">
        <Search size={14} className="absolute left-3 top-3 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="Search teammates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {tab === 'group' && memberIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {memberIds.map((id) => {
            const u = list.find((x) => x._id === id);
            if (!u) return null;
            return (
              <span key={id} className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-xs bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                <span className="w-5 h-5 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center text-[10px] font-semibold">
                  {u.name?.[0]?.toUpperCase()}
                </span>
                {u.name}
                <button type="button" onClick={() => toggle(id)} className="hover:text-rose-500">×</button>
              </span>
            );
          })}
        </div>
      )}

      <div className="max-h-72 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-100 dark:divide-slate-800">
        {users.isLoading && <div className="p-4 text-sm text-slate-500 text-center">Loading...</div>}
        {!users.isLoading && filtered.length === 0 && (
          <div className="p-4 text-sm text-slate-500 text-center">No teammates found</div>
        )}
        {filtered.map((u) => {
          if (tab === 'direct') {
            return (
              <button key={u._id} onClick={() => startDirect(u)} disabled={busy}
                className="w-full flex items-center gap-3 p-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50 text-left">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold">
                  {u.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{u.email}{u.department ? ` · ${u.department}` : ''}</div>
                </div>
                <MessageSquare size={14} className="text-slate-400" />
              </button>
            );
          }
          const checked = memberIds.includes(u._id);
          return (
            <label key={u._id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 text-sm">
              <input type="checkbox" className="rounded" checked={checked} onChange={() => toggle(u._id)} />
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold">
                {u.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{u.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{u.email}{u.department ? ` · ${u.department}` : ''}</div>
              </div>
            </label>
          );
        })}
      </div>
    </Modal>
  );
}
